import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { Link } from "react-router-dom";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import { FETCH_TEAMS_SUBSCRIPTIONS } from "./graphql";
import { formatDateTime } from "./utils";

const SUBSCRIPTIONS_VARIABLES = { includeRemoved: false, limit: 200 };

const SubscriptionsPage = () => {
  const modal = useModals();
  const {
    data: subscriptionsData,
    loading: subscriptionsLoading,
    error: subscriptionsError,
    refetch,
  } = useQuery(FETCH_TEAMS_SUBSCRIPTIONS, {
    variables: SUBSCRIPTIONS_VARIABLES,
    fetchPolicy: "network-only",
  });
  const subscriptions = subscriptionsData?.teamsSubscriptions || [];

  const presentCreateModal = () => {
    modal.present({
      title: "Add Teams Subscription",
      center: true,
      children: (
        <CreateSubscriptionModal
          onCancel={() => modal.hide()}
          onCreated={async () => {
            await refetch();
            modal.hide();
          }}
        />
      ),
    });
  };

  if (subscriptionsLoading) return <Spinner />;
  if (subscriptionsError) return <Errors error={subscriptionsError} />;

  return (
    <Page
      title="Teams Subscriptions"
      rightButtons={
        <Button onClick={presentCreateModal}>+ Add Subscription</Button>
      }
    >
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {!subscriptions.length ? (
          <div className="md:col-span-2 xl:col-span-3 text-center text-gray-500">
            No active subscriptions. Click + Add Subscription to create one.
          </div>
        ) : null}
        {subscriptions.map((subscription) => {
          const detailPath = `${encodeURIComponent(subscription.teamId)}/${encodeURIComponent(subscription.channelId)}`;

          return (
            <div
              key={subscription.subscriptionId}
              className="card flex flex-col"
            >
              <div className="font-semibold opacity-50 text-xs">
                {subscription.status || "Unknown status"}
              </div>
              <h5 className="mt-1">
                {subscription.teamName || subscription.teamId} /{" "}
                {subscription.channelName || subscription.channelId}
              </h5>

              <div className="mt-2 space-y-1 flex-1">
                <div>
                  <span className="opacity-70">Expires:</span>{" "}
                  {formatDateTime(subscription.expirationDatetime)}
                </div>
                <div className="break-all text-xs opacity-70">
                  {subscription.subscriptionId}
                </div>
                {subscription.lastError ? (
                  <div className="text-red-600 break-words">
                    {subscription.lastError}
                  </div>
                ) : null}
              </div>
              <div className="mt-4 flex items-end justify-between">
                <Link
                  className="text-sky-600 hover:text-sky-700"
                  to={detailPath}
                >
                  View Channel
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </Page>
  );
};

export default SubscriptionsPage;
